import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { selectFlowsOnSiteEnabled } from '../../../flow/selectors/FlowSelectors';
import { UserSettingsActions } from '../../../userSettings/services/UserSettingsActions';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { ResultHelper } from '../../../httpClient/utils/ResultHelper';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { ContentFilterKeyName, ContentFiltersKeys } from '../../../ts/models/ContentFilters';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { IServerUserInfo } from '../../../ts/models/IServerUserInfo';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '../../../ts/models/sort/SortOptionDefinitions';
import { ServerUsers } from '../../../userManagement/services/resources/ServerUsers';
import { Users } from '../../../userManagement/services/resources/Users';
import { IGetUserSettingsResult } from '../../../ts/ServerApi/requests/GetUserSettingsRequest';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { SiteRoles } from '../../../ts/ServerApi/types/ISiteRole';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { SiteRoleOptionsResource } from '../../../userManagement/services/resources/SiteRoleOptions';
import { SiteUsers } from '../../../userManagement/services/resources/SiteUsers';
import { setOrderAction } from '../../actions/SetOrderAction';
import { contentCountsUpToDate } from '../../actions/spaces/ContentCountsActions';
import { filterContentType } from '../../actions/spaces/ContentExplorerFilterActions';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { setContentFilterNoRequestContent } from '../../actions/spaces/filters/FilterActions';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { updateBreadcrumbs } from '../../actions/UpdateBreadcrumbsAction';
import { BreadcrumbStateInfo } from '../../content/breadcrumbs/BreadcrumbStateInfo';
import { getDefaultContentTypeFilterValues } from '../../selectors/ContentFilterSelectors';
import { SessionSiteSelectors, SessionUserSelectors } from '../../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { SiteUserSubspaceName, siteUserSubspaceNameToExplorableItemType, SpaceInfo } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { getOrderClausesFromQueryParam } from '../Explorer/ExplorerUrlBindings';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedSiteUserSpace } from './ConnectedSiteUserSpace';
import { SiteUserSpaceOwnProps } from './SiteUserSpace';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { IUserSettings } from '../../../ts/models/IUserSettings';
import { updateUserSettingsAction } from '../../actions/UpdateUserSettingsAction';
import { selectUserSettings } from '../../../userSettings/selectors/UserSettingsSelector';
import { UserSettingsState } from '../../state/UserSettingsState';

export interface SiteUserSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly subspaceName: SiteUserSubspaceName;
  readonly userName: string;
  readonly domainName: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly renderSpace: (props: SiteUserSpaceOwnProps) => JSX.Element;
}

export interface SiteUserSpaceProviderStateProps {
  readonly isSessionUserSelf: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly isSessionSiteAdmin: boolean;
  readonly isFlowsEnabled: boolean;
  readonly userSettings: IUserSettings;
}

export interface SiteUserSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly contentCountsUpToDate: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly filterContentType: (contentTypes: ReadonlyArray<ExplorableItemType>) => void;
  readonly setContentFilterNoRequestContent: (filterType: ContentFilterKeyName, value: string) => void;
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
  readonly updateUserSettingsAction: (username: string, domain: string, userSettings: IUserSettings) => void;
}

interface SiteUserSpaceProverPropsFromAngular {
  readonly sessionManagementService: SessionManagementService;
}

export type SiteUserSpaceNonAngularProps = SiteUserSpaceProviderOwnProps & SiteUserSpaceProviderStateProps & SiteUserSpaceProviderDispatchProps;

export type SiteUserSpaceProviderContainerProps = SiteUserSpaceNonAngularProps & SiteUserSpaceProverPropsFromAngular;

export interface SiteUserSpaceProviderState {
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly siteUser: ISiteUserInfo;
  readonly serverUser: IServerUserInfo;
  readonly hasNoSettings: boolean;
  readonly subspaceName: string;
}

export class SiteUserSpaceProviderContainer extends React.PureComponent<SiteUserSpaceProviderContainerProps, SiteUserSpaceProviderState> {

  private unmounted: boolean = false;
  private readonly initialState: SiteUserSpaceProviderState = {
    initialRender: true,
    renderChildren: false,
    hasError: false,
    siteUser: undefined,
    serverUser: undefined,
    hasNoSettings: false,
    subspaceName: undefined
  };

  public constructor(props: SiteUserSpaceProviderContainerProps) {
    super(props);
    this.state = this.initialState;
  }

  public render() {
    if (this.state.renderChildren) {
      if (this.state.subspaceName === 'settings' && this.state.hasNoSettings) {
        return <Redirect to={this.props.urls.userContentPath(this.props.userName, this.props.domainName)} />;
      }
      return this.renderSpace();
    } else if (this.state.hasError) {
      return <RouterError errorType={'siteuser'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentDidMount(){
    this.getData();
  }

  public componentDidUpdate(prevProps: SiteUserSpaceProviderContainerProps) {
    if (this.userChanged(prevProps)) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace(true);
      this.setState({initialRender: false, renderChildren: true});

    } else if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace(false);
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private changeSubspace(onMount: boolean) {
    let subspaceName = this.props.subspaceName;

    this.updateState({subspaceName});

    let newSpace: SpaceInfo =  {
      item: this.state.siteUser || this.state.serverUser,
      spaceType: 'siteuser',
      subspaceName: subspaceName
    };

    if(!onMount && subspaceName === 'content') {
      this.props.filterContentType(getDefaultContentTypeFilterValues(newSpace, this.props.isFlowsEnabled));
    }

    this.props.enterSpace(newSpace);

    this.updateBreadcrumbs();
    this.setFilterTypesFromUrl();

    this.clearAndSetSortOrderFromURL();


    if (this.props.subspaceName !== 'settings') {
      this.props.requestSpaceContents();
    }
  }

  private updateBreadcrumbs() {
    if (this.props.isSessionServerAdmin || this.props.isSessionSiteAdmin) {
      const breadcrumbs: BreadcrumbStateInfo[] = [
        getSiteUsersPlaceBreadcrumb(this.props.sessionManagementService.isSessionMultiSite(), this.props.urls),
        {
          id: '',
          text: this.state.siteUser ? this.state.siteUser.displayName : this.state.serverUser.displayName
        }
      ];

      this.props.updateBreadcrumbs(breadcrumbs);
    } else {
      const breadcrumbs: BreadcrumbStateInfo[] = [
        {
          text: Messages.backToContent(),
          href: this.props.urls.contentRootUrl(),
          testId: 'back-to-content-link'
        }
      ];
      this.props.updateBreadcrumbs(breadcrumbs);
    }
  }

  private clearAndSetSortOrderFromURL() {
    let contentTypes = [siteUserSubspaceNameToExplorableItemType(this.props.subspaceName) as ExplorableItemType];
    const spaceInfo = {item: undefined, spaceType: 'siteuser', subspaceName: this.props.subspaceName} as SpaceInfo;

    if(this.props.subspaceName === 'content') {
      contentTypes = [...getDefaultContentTypeFilterValues(spaceInfo, this.props.isFlowsEnabled)];
    }

    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);
    const values = queryString.parse(this.props.routeLocationSearch);

    const orderToSet = getOrderClausesFromQueryParam(values[UrlParamKeys.Order], defaultOrder);
    this.props.setOrderAction(orderToSet);
  }

  private setFilterTypesFromUrl() {
    const currentQueryValues = queryString.parse(this.props.routeLocationSearch);
    for (const filterType of _.keys(ContentFiltersKeys)) {
      const value = currentQueryValues[filterType];
      this.props.setContentFilterNoRequestContent(filterType as ContentFilterKeyName, value ? value + '' : undefined);
    }
  }

  private haveAllDataNeededToRender() {
    return (!_.isUndefined(this.state.siteUser) || !_.isUndefined(this.state.serverUser))
      && (this.state.hasNoSettings || !_.isUndefined(this.props.userSettings));
  }

  private userChanged(prevProps: SiteUserSpaceProviderContainerProps): boolean {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.userName) &&
      !_.isUndefined(this.props.domainName) &&
      (prevProps.siteName !== this.props.siteName || prevProps.userName !== this.props.userName || prevProps.domainName !== this.props.domainName);
  }

  private getData = async () => {
    this.getUser();
    this.getUserSettings();
  }

  private getUser = async () => {
    try {
      let user: ISiteUserInfo = undefined;
      if (this.props.isSessionServerAdmin || this.props.isSessionSiteAdmin || this.props.isSessionUserSelf) {
        user = await SiteUsers.instance.getUserByUsernameAndDomain(this.props.userName, this.props.domainName);
      } else  {
        user = await Users.instance.getUserByUsernameAndDomain(this.props.userName, this.props.domainName);
      }

      if (!user && this.props.isSessionServerAdmin) {
        const serverUser = await ServerUsers.instance.getUserByUsernameAndDomain(this.props.userName, this.props.domainName);
        if (serverUser) {
          this.updateState({serverUser});
        } else {
          this.updateState({hasError: true});
        }
      } else if (user) {
        this.updateState({siteUser: user});
      } else {
        this.updateState({hasError: true});
      }

    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getUserSettings = async () => {
    try {
      if (this.props.isSessionServerAdmin || this.props.isSessionSiteAdmin || this.props.isSessionUserSelf) {
        const userSettings = await UserSettingsActions.fetchUserSettings(this.props.userName, this.props.domainName);
        if (ResultHelper.hasErrors(userSettings)) {
          this.updateState({hasNoSettings: true});
        } else {
          this.props.updateUserSettingsAction(this.props.userName, this.props.domainName, userSettings);
        }
      } else {
        this.updateState({hasNoSettings: true});
      }
    } catch (e) {
      this.updateState({hasNoSettings: true});
    }
  }

  private updateState<K extends keyof SiteUserSpaceProviderState>(newState: Pick<SiteUserSpaceProviderState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private renderSpace = () => {
    const props: SiteUserSpaceOwnProps = {
      user: this.state.siteUser || this.state.serverUser,
      siteRole: !_.isUndefined(this.state.siteUser) ? this.state.siteUser.siteRole
                                                    : (!_.isUndefined(this.state.serverUser) ? this.state.serverUser.maxSiteRole
                                                                                             : SiteRoles.ServerAdministrator),
      siteRoleOptions: SiteRoleOptionsResource.instance,
      userSettings: this.props.userSettings,
      isSessionUserSelf: this.props.isSessionUserSelf,
      isSessionServerAdmin: this.props.isSessionServerAdmin,
      isSessionSiteAdmin: this.props.isSessionSiteAdmin,
      isServerDomainFamilyActiveDirectory: ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory(),
      angularContext: this.props.angularContext,
      urls: this.props.urls,
    };
    return this.props.renderSpace(props);
  }
}

export namespace SiteUserSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    return {
      sessionManagementService: getSessionManagementServiceInstance()
    };
  }

  function selectPropsFromAngularServices(angularServices: SiteUserSpaceProverPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<SiteUserSpaceNonAngularProps, SiteUserSpaceProverPropsFromAngular, SiteUserSpaceProverPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const SiteUserSpaceProviderAng: React.ComponentType<SiteUserSpaceNonAngularProps> = SiteUserSpaceProviderAngularProvider.HOC(SiteUserSpaceProviderContainer);

const mapStateToProps: MapStateToProps<SiteUserSpaceProviderStateProps, SiteUserSpaceProviderOwnProps> = (appState, ownProps) => {
  return {
    isSessionUserSelf: SessionUserSelectors.sessionUserName(appState) === ownProps.userName && SessionUserSelectors.sessionDomainName(appState) === ownProps.domainName,
    isSessionServerAdmin: SessionUserSelectors.isSessionServerAdmin(appState),
    isSessionSiteAdmin: SessionSiteSelectors.isSessionSiteAdmin(appState),
    isFlowsEnabled: selectFlowsOnSiteEnabled(appState),
    userSettings: selectUserSettings(appState, ownProps.domainName, ownProps.userName)
  };
};

const mapDispatchToProps: MapDispatchToProps<SiteUserSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
  requestSpaceContents,
  contentCountsUpToDate,
  setOrderAction,
  filterContentType,
  setContentFilterNoRequestContent,
  updateBreadcrumbs,
  updateUserSettingsAction
}, dispatch);

export const ConnectedSiteUserSpaceProviderContainer: React.ComponentClass<SiteUserSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(SiteUserSpaceProviderAng);

const renderSpace = (props: SiteUserSpaceOwnProps): JSX.Element => <ConnectedSiteUserSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type SiteUserSpaceProviderProps = LocationPathProp & Omit<SiteUserSpaceProviderOwnProps, 'renderSpace'>;

export function SiteUserSpaceProvider(props: SiteUserSpaceProviderProps) {
  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateFilters: true,
    shouldUpdateContentTypes: true,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: false
  };

  const spaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'siteuser',
    subspaceName: props.subspaceName
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedSiteUserSpaceProviderContainer {...{...props, renderSpace}} />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
}

function getSiteUsersPlaceBreadcrumb(multiSite: boolean, urls: SpacesSiteUrls): BreadcrumbStateInfo {
  return {
    id: urls && urls.usersUrl(),
    text: multiSite ? Messages.allSiteUsers() : Messages.allUsers(),
    href: urls && urls.usersUrl(),
    testId: 'site-users-link'
  };
}
